import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { InputAdornment, TextField } from '@mui/material';
import styles from './role_assignment.module.css';

const AssignRoleModal = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  initialData,
  title,
  buttonText,
  roles = [],
  users = [],
}) => {
  const [formData, setFormData] = useState({
    role_id: '',
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        role_id: initialData.role_id || initialData.role?.id || '',
      });
      // Set selected user if editing
      if (initialData.user_id || initialData.user?.id) {
        const userId = initialData.user_id || initialData.user?.id;
        const user = users.find((u) => u.id === userId);
        if (user) {
          setSelectedUser({
            id: user.id,
            name: user.name,
            email: user.email,
          });
        }
      }
    } else {
      setFormData({ role_id: '' });
      setSelectedUser(null);
    }
  }, [mode, initialData, users]);

  const handleRoleSelect = (roleId) => {
    setFormData((prev) => ({
      ...prev,
      role_id: roleId,
    }));
  };

  const handleUserToggle = (user) => {
    const userId = user.id;
    const userName = user.name;
    const userEmail = user.email;

    if (selectedUser && selectedUser.id === userId) {
      // Deselect if clicking the same user
      setSelectedUser(null);
    } else {
      // Select new user
      setSelectedUser({
        id: userId,
        name: userName,
        email: userEmail,
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.role_id || !selectedUser) {
      return;
    }

    setIsLoading(true);

    try {
      const roleAssignmentData = {
        role_id: formData.role_id,
        user_id: selectedUser.id,
      };

      // Include role assignment ID when in edit mode
      if (mode === 'edit' && initialData?.id) {
        roleAssignmentData.id = initialData.id;
      }

      const result = await onSubmit(roleAssignmentData);

      // Only close modal and reset fields if the operation was successful
      if (result && result.success) {
        handleModalClose();
      }
    } catch (error) {
      console.error('Error submitting role assignment data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({ role_id: '' });
    setSelectedUser(null);
    setSearchTerm('');
    setIsLoading(false);
    onClose();
  };

  const filteredUsers = users.filter(
    (user) =>
      (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) {
    return null;
  }

  const generateInitials = (name) => {
    if (!name) {
      return '';
    }
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={styles['modal-overlay']} onClick={handleModalClose}>
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles['modal-header']}>
          <h2 className={styles['modal-title']}>{title}</h2>
          <button className={styles['close-button']} onClick={handleModalClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles['modal-body']}>
          {/* Left Section - Select Role */}
          <div className={styles['modal-section']}>
            <h3 className={styles['section-title']}>Select Role</h3>
            <div className={styles['roles-list']}>
              {roles.length > 0 ? (
                roles.map((role) => {
                  const isSelected = role.id === formData.role_id;
                  // Build role display name with region and invoice_type in parentheses
                  let roleDisplayName = role.name;
                  const details = [];
                  if (role.region) {
                    details.push(role.region);
                  }
                  if (role.invoice_type) {
                    details.push(role.invoice_type);
                  }
                  if (details.length > 0) {
                    roleDisplayName += ` (${details.join(', ')})`;
                  }

                  return (
                    <div
                      key={role.id}
                      className={`${styles['role-item']} ${
                        isSelected ? styles['role-item-selected'] : ''
                      }`}
                      onClick={() => handleRoleSelect(role.id)}
                    >
                      <div className={styles['role-name']}>{roleDisplayName}</div>
                    </div>
                  );
                })
              ) : (
                <div className={styles['no-roles-found']}>
                  <p>No roles found</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Section - Select User */}
          <div className={styles['modal-section']}>
            <h3 className={styles['section-title']}>Select User</h3>
            <TextField
              placeholder="Search"
              size="small"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              sx={{
                backgroundColor: '#fff',
                width: '100%',
                '& .MuiInputBase-input': {
                  color: '#999FA7',
                  '::placeholder': {
                    color: '#999FA7',
                    opacity: 1,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  border: '1px solid #ccc',
                  borderRadius: '10px',
                  paddingRight: 0,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#999FA7' }} />
                  </InputAdornment>
                ),
              }}
            />
            <div className={styles['members-list']}>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const userId = user.id;
                  const userName = user.name;
                  const userEmail = user.email;
                  const isSelected = selectedUser && selectedUser.id === userId;

                  return (
                    <div key={userId} className={styles['member-item']}>
                      <label className={styles['member-checkbox']}>
                        <input
                          type="checkbox"
                          checked={isSelected || false}
                          onChange={() => handleUserToggle(user)}
                        />
                        <span className={styles['checkmark']}></span>
                      </label>
                      <div className={styles['member-list-avatar']}>
                        {generateInitials(userName)}
                      </div>
                      <div className={styles['member-info']}>
                        <div className={styles['member-name']}>{userName}</div>
                        <div className={styles['member-email']}>{userEmail}</div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className={styles['no-members-found']}>
                  <p>No users found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className={styles['modal-footer']}>
          <button
            className={styles['cancel-button']}
            onClick={handleModalClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className={styles['create-button']}
            onClick={handleSubmit}
            disabled={isLoading || !formData.role_id || !selectedUser}
          >
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CircularProgress size={16} color="inherit" />
                {mode === 'create' ? 'Assigning...' : 'Updating...'}
              </div>
            ) : (
              buttonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

AssignRoleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    role_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    role: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  }),
  title: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  roles: PropTypes.array,
  users: PropTypes.array,
};

export default AssignRoleModal;
